import './RequiredFieldDropdown.scss';

import React, { useMemo, useState } from 'react';
import { Button, Popup, Label, Menu } from 'semantic-ui-react';
import { useAppSelector } from '../../hooks/ReduxHooks';
import { useActions } from '../../hooks/useActions';
import { setHighlight } from '../actions';
import { getMissingRequiredFieldsViewPositions, ViewPosition } from '../requiredFields';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';
import { DataLayoutPosition, DataLayoutTab, FieldPosition, TemplatePage } from '../../types/template-types';
import ErrorTriangle from '../../icons/ErrorTriangle';

export type RequiredFieldDropdownProps = {
  bringToFieldPosition: (page: TemplatePage, position: FieldPosition) => void;
  bringToDataLayoutPosition: (tab: DataLayoutTab, position: DataLayoutPosition) => void;
};

export default function RequiredFieldDropdown(props: RequiredFieldDropdownProps) {
  const [dropdownOpen, setDropdownOpen] = useState(true);
  const fieldIds = useAppSelector((state) => state.formeditor.missingRequiredFieldIds);
  const formeditorState = useAppSelector((state) => state.formeditor);
  const highlight = useAppSelector((state) => state.formeditor.highlight);
  const setHighlightAction = useActions(setHighlight);
  const viewPositions = useMemo(() => getMissingRequiredFieldsViewPositions(formeditorState), [formeditorState]);

  const handleClick = (viewPosition: ViewPosition) => {
    if (document.body.offsetWidth < 800) {
      setDropdownOpen(false);
    }

    if (viewPosition.fieldPosition != null) {
      props.bringToFieldPosition(viewPosition.page, viewPosition.fieldPosition);
    } else if (viewPosition.dataLayoutPosition != null) {
      props.bringToDataLayoutPosition(viewPosition.tab, viewPosition.dataLayoutPosition);
    }

    analyticsTrack('Required Fields Drop Down Triggered', {
      action: 'Item Clicked'
    });

    setHighlightAction(viewPosition.fieldPosition, viewPosition.dataLayoutPosition);
  };

  const handleOnClose = () => {
    setDropdownOpen(false);
  };

  // defer rendering the menu unless there are items to render
  const renderMenuItems = () => {
    const menuItems = [];

    menuItems.push(
      <div key="header - Incomplete Required Fields" className="header">
        Incomplete Required Fields
      </div>
    );

    let previousGroupName = null;

    for (const viewPosition of viewPositions.filter((x) => x.groupType === 'primary')) {
      if (previousGroupName != viewPosition.groupName) {
        menuItems.push(
          <div key={`primary-${viewPosition.groupName}`} className="header" title={viewPosition.groupName}>
            {viewPosition.groupName}
          </div>
        );
      }
      menuItems.push(
        <button
          key={viewPosition.field.id}
          className={`item${highlight?.fieldId === viewPosition.field.id ? ' highlighted' : ''}`}
          onClick={() => {
            handleClick(viewPosition);
          }}
          title={viewPosition.field.name}
        >
          {viewPosition.field.name}
        </button>
      );
      previousGroupName = viewPosition.groupName;
    }

    previousGroupName = null;

    for (const viewPosition of viewPositions.filter((x) => x.groupType === 'secondary')) {
      if (previousGroupName != viewPosition.groupName) {
        menuItems.push(
          <div key={`secondary-${viewPosition.groupName}`} className="header" title={viewPosition.groupName}>
            {viewPosition.groupName}
          </div>
        );
      }
      menuItems.push(
        <button
          key={viewPosition.field.id}
          className={`item${highlight?.fieldId === viewPosition.field.id ? ' highlighted' : ''}`}
          onClick={() => {
            handleClick(viewPosition);
          }}
          title={viewPosition.field.name}
        >
          {viewPosition.field.name}
        </button>
      );
      previousGroupName = viewPosition.groupName;
    }

    previousGroupName = null;

    for (const viewPosition of viewPositions.filter((x) => x.groupType === 'unmapped')) {
      if (previousGroupName != viewPosition.groupName) {
        menuItems.push(
          <div key={`unmapped-${viewPosition.groupName}`} className="header unmapped" title={viewPosition.groupName}>
            {viewPosition.groupName}
          </div>
        );
      }
      menuItems.push(
        <button key={viewPosition.field.id} className="item unmapped" disabled title={viewPosition.field.name}>
          {viewPosition.field.name}
        </button>
      );
      previousGroupName = viewPosition.groupName;
    }

    return menuItems;
  };

  if (!viewPositions || viewPositions.length === 0) {
    return null;
  }

  return (
    <Popup
      className="required-fields-dropdown-popup"
      trigger={
        <div className="item">
          <Button
            className="tertiary drop-down-button"
            onClick={() => {
              analyticsTrack('Required Fields Drop Down Triggered', {
                action: dropdownOpen ? 'Close Dropdown' : 'Open Dropdown'
              });
              setDropdownOpen(!dropdownOpen);
            }}
          >
            <Menu text>
              <Menu.Item className="error-triangle-menu-item" fitted>
                <ErrorTriangle />
                <Label
                  size="mini"
                  className={`error-triangle-numbers${fieldIds.length < 10 ? '' : fieldIds.length < 100 ? ' double-margin' : ' triple-margin'}`}
                  circular
                  floating
                >
                  <div className="error-triangle-number">{fieldIds.length > 99 ? '99+' : fieldIds.length}</div>
                </Label>
              </Menu.Item>
            </Menu>
          </Button>
        </div>
      }
      basic
      offset={[3, -10]}
      position="bottom right"
      open={dropdownOpen}
      onClose={handleOnClose}
      closeOnDocumentClick
      closeOnTriggerBlur={false}
      closeOnTriggerMouseLeave={false}
      closeOnPortalMouseLeave={false}
    >
      <div className="required-fields-dropdown">{renderMenuItems()}</div>
    </Popup>
  );
}
